import React from "react";
import "./index.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Auth from "./components/Auth";
import FullScreenLoader from "./components/common/loadingPWA";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
// import PushNotificationSetup from "./components/PushNotificationSetup";
import { SubscriptionProvider } from "./providers/SubscriptionProvider";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import CompleteProfile from "./components/CompleteProfile";
import EditProfile from "./components/EditProfile";
import Dashboard from "./pages/Dashboard";
import Feed from "./pages/Feed";
import Connections from "./pages/Connections";
import Chat from "./pages/Chat";
import Chats from "./pages/Chats";
import DataVisualiser from "./pages/DataVisualiser";
import DailyReport from "./pages/DailyReport";
import Support from "./pages/Support";
import Sessions from "./pages/Sessions";
import CompletedSessions from "./pages/CompletedSessions";
import Activities from "./pages/Activities";
import Performances from "./pages/Performances";
import TrainingLogNew from "./pages/TrainingLogNew";
import Session from "./pages/Session";
import Programs from "./pages/Programs";
import ProgramDetail from "./pages/ProgramDetail";
import NewProgram from "./pages/NewProgram";
import Notifications from "./pages/Notifications";
import Invitations from "./pages/Invitations";
import Settings from "./pages/Settings";
import HeatmapFullYear from "./pages/HeatmapFullYear";
import {
  WearableSettings,
  NotificationSettings,
  EQLXSettings,
  PhysicalTestSettings,
  TrainingTagsSettings,
  SubscriptionSettings,
  GoalsSettings,
} from "./pages/settings/index";
import AthletePreferences from "./pages/settings/AthletePreferences";
import PrivacySettings from "./pages/settings/PrivacySettings";
import CoachesCornerSettings from "./pages/settings/CoachesCornerSettings";
import Updates from "./pages/Updates";
import { OfflineDemo } from "./components/OfflineDemo";
import PrivateRoute from "./components/PrivateRoute";
import { AdminPage, AdminRoute } from "./pages/admin";
import { useEffect, useState, useCallback, createContext } from "react";
import { notificationService } from "./services/notificationService";
import { analyticsService } from "./services/analyticsService";
import { metaPixelService } from "./services/metaPixelService";
import {
  safeGetItem,
  safeSetItem,
  safeSessionGetItem,
  safeSessionSetItem,
} from "./utils/localStorageUtils";
import Circles from "./pages/Circles";
import Coach from "./pages/Coach";
import Explore from "./pages/PublicFeed";
import Share from "./pages/Share";
import WellnessInsights from "./pages/insights/WellnessInsights";
import SleepInsights from "./pages/insights/SleepInsights";
import PeakInsights from "./pages/insights/PeakInsights";
import TrainingInsights from "./pages/insights/TrainingInsights";
import TrainingLogInsights from "./pages/insights/TrainingLogInsights";
import DailyInsights from "./pages/insights/DailyInsights";
import ActivityExerciseInsights from "./pages/insights/ActivityExerciseInsights";
import LandingPage from "./pages/landing/LandingPage";
import ConditionalRoute from "./components/ConditionalRoute";
import Example from "./components/dashboard/dataExpanded/example";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthHandler from "./pages/AuthHandler";
import PairWatch from "./pages/PairWatch";
import AiSettings from "./pages/settings/AiSettings";
import AppearanceSettings from "./pages/settings/AppearanceSettings";
import Security from "./pages/settings/Security";
import MyReports from "./pages/settings/MyReports";
import "./stores/themeStore"; // Initialize theme on app load
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import PeakScorePage from "./pages/PeakScorePage";
import Profile from "./pages/Profile";
import PostPage from "./pages/Post";
import AccountDeactivated from "./components/AccountDeactivated";
import CompleteAccountRemoval from "./components/CompleteAccountRemoval";
import SessionsOld from "./pages/SessionsOld";
import Schedule from "./pages/Schedule";
import STAXSPage from "./pages/staxs/staxs";
import ExplorePage from "./pages/staxs/Explore";
import MintPage from "./pages/staxs/Mint";
import CyclePage from "./pages/Cycle";
import ConnectionResult from "./pages/ConnectionResult";
import DownloadData from "./pages/DownloadData";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import SessionBuilder from "./pages/SessionBuilder";

import { TourProvider, useTour } from "@reactour/tour";
import InboxPage from "./pages/InboxPage";
import NotificationOrchestrator from "./NotificationOrchestrator";

import SubscriptionChangeNotice from "./components/SubscriptionChangeNotice";

import Cookies, {
  CONSENT_EVENT,
  CONSENT_KEY,
  CookiePrefs,
} from "./components/compliance/Cookies";
import { analyticsPath, isAuthPath } from "./utils/analyticsUrl";
import { isInsightsPath } from "./utils/retention";

export const WalkthroughContext = createContext<{ startTour: () => void }>({
  startTour: () => {},
});

const DEFAULT_COOKIE_PREFS: CookiePrefs = {
  essential: true,
  analytics: false,
  marketing: false,
};

const getStoredCookiePrefs = (): CookiePrefs => {
  try {
    const rawPrefs = localStorage.getItem(CONSENT_KEY);
    if (!rawPrefs) return DEFAULT_COOKIE_PREFS;

    const parsed = JSON.parse(rawPrefs);
    return {
      essential: true,
      analytics: Boolean(parsed?.analytics),
      marketing: Boolean(parsed?.marketing),
    };
  } catch {
    return DEFAULT_COOKIE_PREFS;
  }
};

function WalkthroughProvider({ children }: { children: React.ReactNode }) {
  const { setIsOpen } = useTour();

  const startTour = () => {
    // ✅ Open the tour dynamically
    setTimeout(() => setIsOpen(true), 200); // short delay to ensure target exists
  };

  return (
    <WalkthroughContext.Provider value={{ startTour }}>
      {children}
    </WalkthroughContext.Provider>
  );
}
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/peak-score" element={<PeakScorePage />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth" element={<AuthHandler />} />
      {/* Registration + onboarding wizard. Public: the Auth account is created
          mid-flow, so signed-out visitors must be able to start here. */}
      <Route path="/onboarding" element={<OnboardingWizard />} />
      <Route path="/connectionsuccess" element={<ConnectionResult />} />
      <Route path="/connectionfailed" element={<ConnectionResult />} />
      <Route path="/connectionresult" element={<ConnectionResult />} />
      {/* <Route path="/account-deactivated" element={<AccountDeactivated />} /> */}
      <Route
        path="/account-deactivated"
        element={
          <PrivateRoute>
            <AccountDeactivated />
          </PrivateRoute>
        }
      />
      <Route
        path="/complete-account-removal"
        element={
          <PrivateRoute>
            <CompleteAccountRemoval />
          </PrivateRoute>
        }
      />
      <Route
        path="/complete-profile"
        element={
          <PrivateRoute>
            <CompleteProfile />
          </PrivateRoute>
        }
      />
      <Route
        path="/edit-profile"
        element={
          <PrivateRoute>
            <EditProfile />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      {/* EXPANDED DATA from dashbaord */}
      <Route
        path="/example"
        element={
          <PrivateRoute>
            <Example />
          </PrivateRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <PrivateRoute>
            <Feed />
          </PrivateRoute>
        }
      />
      <Route
        path="/link"
        element={
          <PrivateRoute>
            <PairWatch />
          </PrivateRoute>
        }
      />
      <Route
        path="/connections"
        element={
          <PrivateRoute>
            <Connections />
          </PrivateRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <PrivateRoute>
            <Explore />
          </PrivateRoute>
        }
      />
      <Route
        path="/planner"
        element={
          <PrivateRoute>
            <SessionsOld />
          </PrivateRoute>
        }
      />
      <Route
        path="/share"
        element={
          <PrivateRoute>
            <Share />
          </PrivateRoute>
        }
      />
      <Route
        path="/chats"
        element={
          <PrivateRoute>
            <Chats />
          </PrivateRoute>
        }
      />
      <Route
        path="/chats/:chatId"
        element={
          <PrivateRoute>
            <Chat />
          </PrivateRoute>
        }
      />
      <Route
        path="/circles"
        element={
          <PrivateRoute>
            <Circles />
          </PrivateRoute>
        }
      />
      <Route
        path="/heatmap"
        element={
          <PrivateRoute>
            <HeatmapFullYear />
          </PrivateRoute>
        }
      />
      <Route
        path="/coach"
        element={
          <PrivateRoute>
            <Coach />
          </PrivateRoute>
        }
      />
      <Route
        path="/data-visualiser"
        element={
          <PrivateRoute>
            <DataVisualiser />
          </PrivateRoute>
        }
      />
      <Route
        path="/dailyreport"
        element={
          <PrivateRoute>
            <DailyReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/support"
        element={
          <PrivateRoute>
            <Support />
          </PrivateRoute>
        }
      />
      <Route
        path="/cycle"
        element={
          <PrivateRoute>
            <CyclePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <PrivateRoute>
            <Sessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/sessions/completed"
        element={
          <PrivateRoute>
            <CompletedSessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/activities"
        element={
          <PrivateRoute>
            <Activities />
          </PrivateRoute>
        }
      />
      <Route
        path="/performances"
        element={
          <PrivateRoute>
            <Performances />
          </PrivateRoute>
        }
      />
      <Route
        path="/training-log-new"
        element={
          <PrivateRoute>
            <TrainingLogNew />
          </PrivateRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <PrivateRoute>
            <Schedule />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs"
        element={
          <PrivateRoute>
            <Programs />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs/new"
        element={
          <PrivateRoute>
            <NewProgram />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs/:programId/detail"
        element={
          <PrivateRoute>
            <ProgramDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/programs/:programId"
        element={
          <PrivateRoute>
            <NewProgram />
          </PrivateRoute>
        }
      />
      <Route
        path="/session/:sessionId"
        element={
          <PrivateRoute>
            <Session />
          </PrivateRoute>
        }
      />
      <Route
        path="/sessionbuilder"
        element={
          <PrivateRoute>
            <SessionBuilder />
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <PrivateRoute>
            <Notifications />
          </PrivateRoute>
        }
      />
      <Route
        path="/invitations"
        element={
          <PrivateRoute>
            <Invitations />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/privacy"
        element={
          <PrivateRoute>
            <PrivacySettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/wearable"
        element={
          <PrivateRoute>
            <WearableSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <PrivateRoute>
            <NotificationSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/eqlx"
        element={
          <PrivateRoute>
            <EQLXSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/staxs"
        element={
          <PrivateRoute>
            <STAXSPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staxs/explore"
        element={
          <PrivateRoute>
            <ExplorePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staxs/mint"
        element={
          <PrivateRoute>
            <MintPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/physical-test"
        element={
          <PrivateRoute>
            <PhysicalTestSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/training-tags"
        element={
          <PrivateRoute>
            <TrainingTagsSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/ai"
        element={
          <PrivateRoute>
            <AiSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/subscription"
        element={
          <PrivateRoute>
            <SubscriptionSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/goals"
        element={
          <PrivateRoute>
            <GoalsSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/athlete-preferences"
        element={
          <PrivateRoute>
            <AthletePreferences />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/coaches-corner"
        element={
          <PrivateRoute>
            <CoachesCornerSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/security"
        element={
          <PrivateRoute>
            <Security />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/my-reports"
        element={
          <PrivateRoute>
            <MyReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/appearance"
        element={
          <PrivateRoute>
            <AppearanceSettings />
          </PrivateRoute>
        }
      />
      <Route
        path="/updates"
        element={
          <PrivateRoute>
            <Updates />
          </PrivateRoute>
        }
      />
      <Route
        path="/offline-demo"
        element={
          <PrivateRoute>
            <OfflineDemo />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/insights/wellness"
        element={
          <PrivateRoute>
            <WellnessInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/sleep"
        element={
          <PrivateRoute>
            <SleepInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/downloaddata"
        element={
          <PrivateRoute>
            <DownloadData />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/peak"
        element={
          <PrivateRoute>
            <PeakInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/training"
        element={
          <PrivateRoute>
            <TrainingInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/daily"
        element={
          <PrivateRoute>
            <DailyInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/activities"
        element={
          <PrivateRoute>
            <ActivityExerciseInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/insights/trainingLog/:trainingLogId"
        element={
          <PrivateRoute>
            <TrainingLogInsights />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route path="/post/:ownerUserId/:postId" element={<PostPage />} />
      <Route path="/post/:postId" element={<PostPage />} />
      <Route
        path="/inbox"
        element={
          <PrivateRoute>
            <InboxPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/"
        element={
          <ConditionalRoute>
            <Dashboard />
          </ConditionalRoute>
        }
      />
      <Route path="*" element={<Navigate to="/landing" />} />
    </Routes>
  );
};
let walkthroughActive = false;
function useServiceWorkerReady() {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    async function waitForSW() {
      if (!("serviceWorker" in navigator)) {
        setReady(true);
        return;
      }

      // Wait until SW is active
      await navigator.serviceWorker.ready;

      // Wait until SW controls the page
      if (!navigator.serviceWorker.controller) {
        await new Promise<void>((resolve) => {
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => resolve(),
            { once: true },
          );
        });
      }

      // Reduced delay: just enough for FCM internals to initialize
      // 4000ms was too aggressive — 1500ms is sufficient
      await new Promise((r) => setTimeout(r, 1500));

      if (!cancelled) setReady(true);
    }

    waitForSW();
    return () => {
      cancelled = true;
    };
  }, []);

  return ready;
}
const AppContent = () => {
  const swReady = useServiceWorkerReady();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isWalkthrough = searchParams.get("walkthrough") === "true";
  const { currentUser, authState, initializing } = useAuth();
  const isLoginPage = location.pathname === "/login";
  const isAdminPage = location.pathname === "/admin";
  const isSharePage = location.pathname === "/share";
  const isOnboardingPage = location.pathname.startsWith("/onboarding");
  const isConnectionResultPage =
    location.pathname === "/connectionresult" ||
    location.pathname === "/connectionsuccess" ||
    location.pathname === "/connectionfailed";
  const [marketingTrackingEnabled, setMarketingTrackingEnabled] =
    useState(false);

  useEffect(() => {
    const applyConsentPrefs = (prefs: CookiePrefs) => {
      analyticsService.setConsent(prefs.analytics);

      if (prefs.marketing) {
        metaPixelService.enable();
      } else {
        metaPixelService.disable();
      }

      setMarketingTrackingEnabled(prefs.marketing);
    };

    const syncFromStorage = () => {
      applyConsentPrefs(getStoredCookiePrefs());
    };

    const handleConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent<CookiePrefs>;
      const nextPrefs = customEvent.detail;
      if (nextPrefs) {
        applyConsentPrefs(nextPrefs);
        return;
      }
      syncFromStorage();
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === CONSENT_KEY) {
        syncFromStorage();
      }
    };

    syncFromStorage();
    window.addEventListener(
      CONSENT_EVENT,
      handleConsentChange as EventListener,
    );
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        CONSENT_EVENT,
        handleConsentChange as EventListener,
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Track page views when location changes
  useEffect(() => {
    analyticsService.trackPageView(analyticsPath(location.pathname, location.search));
    // The Meta Pixel reports the full page URL, so it never runs on auth pages.
    if (marketingTrackingEnabled && !isAuthPath(location.pathname)) {
      metaPixelService.trackPageView();
    }
  }, [location, marketingTrackingEnabled]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    notificationService.recordAppOpen();

    const handleVisibilityChange = () => {
      if (!document.hidden) notificationService.recordAppOpen();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (currentUser?.uid && isInsightsPath(location.pathname)) {
      notificationService.recordInsightsViewed();
    }
  }, [currentUser?.uid, location.pathname]);

  // Foreground notification state
  const [foregroundNotification, setForegroundNotification] = useState<{
    title: string;
    body: string;
  } | null>(null);

  // Initialize Intercom with user data
  useEffect(() => {
    const initializeIntercom = () => {
      // Check if Intercom is already loaded
      if (window.Intercom) {
        return;
      }

      // Replace with your actual Intercom app ID
      const INTERCOM_APP_ID =
        process.env.REACT_APP_INTERCOM_APP_ID || "wiedyikd";

      // Create Intercom script
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

      // Add script to head
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }

      // Initialize Intercom when script loads
      // Note: The script itself may try to access localStorage during initialization
      // This is handled by the global error handler in index.tsx
      script.onload = () => {
        try {
          if (window.Intercom) {
            // Prepare Intercom configuration
            const intercomConfig: any = {
              app_id: INTERCOM_APP_ID,
              hide_default_launcher: true, // Hide the chat icon globally by default
            };

            // Add user data if user is authenticated
            if (currentUser) {
              intercomConfig.user_id = currentUser.uid;
              intercomConfig.email = currentUser.email;
              intercomConfig.name =
                currentUser.displayName ||
                currentUser.email?.split("@")[0] ||
                "User";
              intercomConfig.created_at = Math.floor(
                new Date(
                  currentUser.metadata.creationTime || Date.now(),
                ).getTime() / 1000,
              );

              intercomConfig.custom_attributes = {
                user_type: "authenticated",
                signup_date: currentUser.metadata.creationTime,
                last_login: currentUser.metadata.lastSignInTime,
                email_verified: currentUser.emailVerified,
              };

              console.log("Initializing Intercom globally with user data:", {
                user_id: intercomConfig.user_id,
                email: intercomConfig.email,
                name: intercomConfig.name,
              });
            } else {
              // For anonymous users
              const anonymousId =
                safeGetItem("intercom_anonymous_id") ||
                `anonymous_${Date.now()}_${Math.random()
                  .toString(36)
                  .substring(2, 11)}`;
              safeSetItem("intercom_anonymous_id", anonymousId);

              intercomConfig.user_id = anonymousId;
              intercomConfig.name = "Anonymous User";
              intercomConfig.custom_attributes = {
                user_type: "anonymous",
              };

              console.log(
                "Initializing Intercom globally with anonymous user:",
                anonymousId,
              );
            }

            // Wrap Intercom boot in try-catch to handle localStorage errors
            try {
              window.Intercom("boot", intercomConfig);
              // Dispatch custom event to notify components that Intercom is ready
              window.dispatchEvent(new CustomEvent("intercomReady"));
            } catch (bootError: any) {
              // Handle SecurityError from Intercom SDK accessing localStorage
              if (
                bootError?.name === "SecurityError" ||
                bootError?.message?.includes("localStorage") ||
                bootError?.message?.includes("Access is denied")
              ) {
                console.warn(
                  "Intercom boot skipped due to localStorage access restrictions:",
                  bootError,
                );
                // Continue without Intercom - app will still work
              } else {
                throw bootError;
              }
            }
          }
        } catch (error) {
          console.error("Error initializing Intercom:", error);
          // App continues to work even if Intercom fails
        }
      };

      script.onerror = () => {
        console.error("Failed to load Intercom script");
      };
    };

    // Only initialize in production or when explicitly enabled
    if (
      process.env.NODE_ENV === "production" ||
      process.env.REACT_APP_ENABLE_INTERCOM === "true"
    ) {
      initializeIntercom();
    }

    // Cleanup function
    return () => {
      if (window.Intercom) {
        window.Intercom("shutdown");
      }
    };
  }, [currentUser]); // Re-run when currentUser changes

  const isProduction =
    process.env.REACT_APP_FORCE_PRODUCT_MODE === "true" ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "peakxv2.web.app" ||
        window.location.hostname === "peak.app" ||
        window.location.hostname === "www.peak.app"));
  const trackCampaignClickEndpoint = isProduction
    ? "https://us-central1-peak-x-937b6.cloudfunctions.net/trackCampaignClick"
    : "https://us-central1-peak-x-staging.cloudfunctions.net/trackCampaignClick";

  // Track campaign click
  const trackCampaignClick = async (campaignCode: string) => {
    console.log(
      "🎯 trackCampaignClick called for:",
      campaignCode,
      "at",
      new Date().toISOString(),
    );

    try {
      const response = await fetch(trackCampaignClickEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ campaignCode }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log(
          "✅ Campaign click tracked successfully:",
          campaignCode,
          result,
        );
      } else {
        console.warn(
          "❌ Failed to track campaign click:",
          await response.text(),
        );
      }
    } catch (error) {
      console.error("💥 Error tracking campaign click:", error);
    }
  };

  // Handle campaign code from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const codeParam = urlParams.get("code");

    // console.log("useEffect triggered, codeParam:", codeParam);

    if (codeParam) {
      // Check if we've already processed this campaign code in this session
      const sessionKey = `campaign_tracked_${codeParam}`;
      const alreadyTracked = safeSessionGetItem(sessionKey);

      if (alreadyTracked) {
        console.log(
          "Campaign click already tracked in this session:",
          codeParam,
        );
      } else {
        console.log("Processing new campaign code:", codeParam);

        // Mark as tracked in session storage
        safeSessionSetItem(sessionKey, Date.now().toString());

        // Store campaign code in localStorage
        safeSetItem("campaignCode", codeParam);
        console.log("Campaign code stored:", codeParam);

        // Track the campaign click (both backend and analytics)
        trackCampaignClick(codeParam);
        analyticsService.trackCampaign(codeParam);
      }

      // Remove the code parameter from URL to clean it up
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location.search]);

  // Setup foreground message listener
  const handleForegroundMessage = useCallback(
    (payload: any) => {
      console.log("Foreground message received:", payload);

      // Show in-app notification
      setForegroundNotification({
        title: payload.notification?.title || "Remember to log!",
        body:
          payload.notification?.body ||
          "Don't forget to log your daily activities!",
      });
    },
    [setForegroundNotification],
  );

  // Setup custom notification event listener
  const handleCustomNotification = useCallback(
    (event: CustomEvent) => {
      const { title, message, duration = 3000 } = event.detail;

      // Show notification using existing toast system
      setForegroundNotification({
        title,
        body: message || "",
      });

      // Auto-dismiss after duration if specified
      if (duration > 0) {
        setTimeout(() => {
          setForegroundNotification(null);
        }, duration);
      }
    },
    [setForegroundNotification],
  );

  useEffect(() => {
    const unsubscribe = notificationService.setupForegroundMessageListener(
      handleForegroundMessage,
    );

    // Listen for custom notification events
    window.addEventListener(
      "showAppNotification",
      handleCustomNotification as EventListener,
    );

    return () => {
      if (unsubscribe) unsubscribe();
      window.removeEventListener(
        "showAppNotification",
        handleCustomNotification as EventListener,
      );
    };
  }, [handleForegroundMessage, handleCustomNotification]);

  const handleNotificationClick = () => {
    setForegroundNotification(null);
    navigate("/dashboard");
  };

  const handleNotificationClose = () => {
    setForegroundNotification(null);
  };

  // Show loading screen only while the initial session check is unresolved.
  // authState.loading also flips true/false on every login/signup/logout
  // action; gating on it here would unmount the whole app (wiping in-progress
  // form state, e.g. the login form) on every attempt, not just on first load.
  if (initializing) {
    return <FullScreenLoader isLoading={true} />;
  }
  return (
    <div className="flex flex-col min-h-screen bg-base-300">
      <SubscriptionChangeNotice />
      <div
        className={`
        flex-1 overflow-y-auto
        ${isAdminPage ? "w-full px-0" : "w-full mx-auto "}
      `}
        style={{
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        {!isLoginPage &&
          !isAdminPage &&
          !isSharePage &&
          !isOnboardingPage &&
          !isConnectionResultPage &&
          !isWalkthrough && <PwaInstallPrompt />}
        <AppRoutes />
      </div>
      <Cookies />

      {/* Push Notification Setup - runs at app level so device token refresh
          completes even if the user navigates away from Dashboard quickly */}
      {currentUser && swReady && <NotificationOrchestrator />}

      {/* Foreground notification toast */}
      {foregroundNotification && (
        <div className="toast toast-bottom toast-center z-50">
          <div
            className="bg-base-200 p-4 shadow-xl rounded-xl border-2 cursor-pointer hover:bg-blue-500/15 transition-colors"
            onClick={handleNotificationClick}
          >
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationClose();
              }}
            >
              ✕
            </button>
            <div>
              <div className="font-bold font-grotesk">
                {foregroundNotification.title}
              </div>
              <div className="text-sm">{foregroundNotification.body}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  const steps = [
    {
      selector: "#peak-title",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Peak Readiness Score</h3>
          <p className="text-sm mb-2">
            The Peak Score is your readiness to perform. High is good, low is
            not ideal. It's calculated based on your daily wellness and training
            load. The more you log, the more accurate your score will be. It's
            completely relative to you.
          </p>
        </div>
      ),
    },
    {
      selector: "#wellness-title",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Wellness Score</h3>
          <p className="text-sm mb-2">
            Your wellness score appears after you log your daily quesionnaire.
            The most accurate measure of your wellness is actually you. So we
            ask how you feel each day to track your wellness.
          </p>
        </div>
      ),
    },
    {
      selector: "#load-title",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Load</h3>
          <p className="text-sm mb-3">
            Load is your Perceived Training Effort (RPE) x Time (mins). This is
            logged for each training session and used to calculate your Peak
            Score.
          </p>
        </div>
      ),
    },

    {
      selector: "#training-sessions",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Logged Training Sessions</h3>
          <p className="text-sm mb-3">
            All your daily training logs and wearable activity data appears
            here.
          </p>
        </div>
      ),
    },
    {
      selector: "#top-container",
      highlightedSelectors: ["#sleep-container", "#training-container"],

      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Load & Sleep Goals</h3>
          <p className="text-sm mb-2">
            Monitor daily targets visually in your dashboard. Goals can be
            adjusted in settings.
          </p>
        </div>
      ),
    },
    //  {
    //   selector: "#training-container",
    //   content: (
    //  <div className="max-w-xs text-gray-800 pt-4 pr-4">
    //   <h3>Load Goals TO BE REMOVED</h3>
    //   <p className="text-md font-heading mb-2">
    //     Track your daily training load goals and set a target in your settings. This would be dependent on your training opjectives and periodisation.
    //   </p>
    // </div>

    // )
    // },

    {
      selector: isMobile ? "#diary-title-mobile" : "#diary-title",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Training Diary Feed</h3>
          <p className="text-sm mb-3">
            Scroll through your training diary to view, edit and search all your
            logs.
          </p>
        </div>
      ),
    },
    {
      selector: isMobile ? "#wizard-mobile" : "#wizard",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>The Wizard AI</h3>
          <p className="text-sm mb-3">
            The Wizard is your AI helper for data that provides personalised
            insights and education.
          </p>
        </div>
      ),
    },
    {
      selector: "#wearable-container",
      content: (
        <div className="max-w-xs text-base-content pt-4 pr-4">
          <h3>Wearable Fitness Devices</h3>
          <p className="text-sm mb-3">
            Connect popular wearable devices like Garmin, Whoop, Oura and more
            enhance your data and insights.
          </p>
        </div>
      ),
    },
  ];

  // return (
  //   <BrowserRouter>
  //     <AuthProvider>
  //       <div className="z-45">
  //         <AppContent />
  //       </div>
  //     </AuthProvider>
  //   </BrowserRouter>
  // );
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <TourProvider
            steps={steps}
            padding={8}
            styles={{
              popover: (base) => ({
                ...base,
                borderRadius: "12px",
                border: "2px solid #667eea",
                backgroundColor: "oklch(var(--b1))",
                color: "oklch(var(--bc))",
                boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                padding: "1rem",
              }),
              maskArea: (base) => ({
                ...base,
                rx: 12, // rounded mask corners
              }),
              maskWrapper: (base) => ({
                ...base,
                color: "rgba(0,0,0,0.7)", // overlay background
              }),
              badge: (base) => ({
                ...base,
                backgroundColor: "#667eea",
                color: "#fff",
              }),
            }}
          >
            <WalkthroughProvider>
              <div className="z-45">
                <AppContent />
              </div>
            </WalkthroughProvider>
          </TourProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
